import * as React from 'react';

import type { Transport } from '@connectrpc/connect';
import { peerIdFromString } from '@libp2p/peer-id';
import type { Libp2p } from 'libp2p';

import { createLibp2pNode } from './createNode';
import { Libp2pContext } from './Libp2pContext';
import {
  createRpcTransport,
  type CreateRpcClientOptions,
} from './RpcTransport';
import type { Libp2pContextValue, SidecarConnection } from './types';

type SidecarConnectionInternal = SidecarConnection & {
  transport: Transport;
};

export const Libp2pProvider = ({ children }: React.PropsWithChildren) => {
  const [nodeReady, setNodeReady] = React.useState(false);
  const nodeRef = React.useRef<Libp2p | null>(null);
  const connectionsRef = React.useRef(
    new Map<string, SidecarConnectionInternal>(),
  );
  const pendingConnectionsRef = React.useRef(new Map<string, Promise<void>>());
  const [connections, setConnections] = React.useState(
    new Map<string, SidecarConnection>(),
  );

  const syncConnections = React.useCallback(() => {
    const snapshot = new Map<string, SidecarConnection>();
    for (const [id, conn] of connectionsRef.current) {
      snapshot.set(id, {
        sidecarId: conn.sidecarId,
        peerId: conn.peerId,
        type: conn.type,
        connected: conn.connected,
      });
    }
    setConnections(snapshot);
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const node = await createLibp2pNode();
      if (cancelled) {
        await node.stop();
        return;
      }
      nodeRef.current = node;

      node.addEventListener('peer:disconnect', (event) => {
        const disconnectedPeerId = event.detail.toString();
        let changed = false;
        for (const conn of connectionsRef.current.values()) {
          if (conn.peerId === disconnectedPeerId && conn.connected) {
            conn.connected = false;
            changed = true;
          }
        }
        if (changed) syncConnections();
      });

      node.addEventListener('peer:connect', (event) => {
        const connectedPeerId = event.detail.toString();
        let changed = false;
        for (const conn of connectionsRef.current.values()) {
          if (conn.peerId === connectedPeerId && !conn.connected) {
            conn.connected = true;
            changed = true;
          }
        }
        if (changed) syncConnections();
      });

      setNodeReady(true);
    };

    init();

    return () => {
      cancelled = true;
      pendingConnectionsRef.current.clear();
      connectionsRef.current.clear();
      nodeRef.current?.stop();
      nodeRef.current = null;
    };
  }, [syncConnections]);

  const closePeerConnections = React.useCallback(async (peerId: string) => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }

    const parsedPeerID = peerIdFromString(peerId);
    const activeConnections = node.getConnections(parsedPeerID);
    await Promise.all(
      activeConnections.map((conn) =>
        conn.close().catch((error) => {
          console.warn('libp2p close connection failed', {
            peerId,
            error,
          });
        }),
      ),
    );
  }, []);

  const disconnect = React.useCallback(
    async (sidecarId: string) => {
      const pending = pendingConnectionsRef.current.get(sidecarId);
      if (pending) {
        try {
          await pending;
        } catch {
          /* ignored */
        }
      }

      const existing = connectionsRef.current.get(sidecarId);
      if (!existing) {
        return;
      }

      connectionsRef.current.delete(sidecarId);
      syncConnections();

      await closePeerConnections(existing.peerId);
    },
    [closePeerConnections, syncConnections],
  );

  const connect = React.useCallback(
    async (
      sidecarId: string,
      peerId: string,
      multiaddrs: string[],
      options?: CreateRpcClientOptions,
    ) => {
      const existing = connectionsRef.current.get(sidecarId);
      if (existing?.connected && existing.peerId === peerId) {
        return;
      }

      const pending = pendingConnectionsRef.current.get(sidecarId);
      if (pending) {
        return pending;
      }

      const attempt = (async () => {
        const node = nodeRef.current;
        if (!node) {
          throw new Error('libp2p node not ready');
        }

        if (existing && existing.peerId !== peerId) {
          await closePeerConnections(existing.peerId);
        }

        const { transport, connectionType } = await createRpcTransport(
          node,
          peerId,
          multiaddrs,
          options,
        );

        connectionsRef.current.set(sidecarId, {
          sidecarId,
          peerId,
          type: connectionType,
          connected: true,
          transport,
        });
        syncConnections();
      })();

      pendingConnectionsRef.current.set(sidecarId, attempt);
      try {
        await attempt;
      } finally {
        pendingConnectionsRef.current.delete(sidecarId);
      }
    },
    [closePeerConnections, syncConnections],
  );

  const getConnection = React.useCallback((sidecarId: string) => {
    const conn = connectionsRef.current.get(sidecarId);
    if (!conn) return undefined;
    return {
      sidecarId: conn.sidecarId,
      peerId: conn.peerId,
      type: conn.type,
      connected: conn.connected,
    };
  }, []);

  const getTransport = React.useCallback(
    (sidecarId: string) => connectionsRef.current.get(sidecarId)?.transport,
    [],
  );

  const getNode = React.useCallback(() => nodeRef.current, []);

  const upgradeConnection = React.useCallback(
    async (
      sidecarId: string,
      peerId: string,
      directMultiaddrs: string[],
      options?: CreateRpcClientOptions,
    ): Promise<boolean> => {
      const existing = connectionsRef.current.get(sidecarId);
      if (!existing || !existing.connected || existing.type !== 'relayed') {
        return false;
      }

      if (directMultiaddrs.length === 0) {
        return false;
      }

      const node = nodeRef.current;
      if (!node) {
        return false;
      }

      try {
        const { transport, connectionType } = await createRpcTransport(
          node,
          peerId,
          directMultiaddrs,
          options,
        );

        if (connectionType !== 'direct') {
          return false;
        }

        await closePeerConnections(existing.peerId);

        connectionsRef.current.set(sidecarId, {
          sidecarId,
          peerId,
          type: 'direct',
          connected: true,
          transport,
        });
        syncConnections();
        return true;
      } catch {
        return false;
      }
    },
    [closePeerConnections, syncConnections],
  );

  const value = React.useMemo<Libp2pContextValue>(
    () => ({
      nodeReady,
      getNode,
      connect,
      disconnect,
      getConnection,
      getTransport,
      upgradeConnection,
      connections,
    }),
    [
      nodeReady,
      getNode,
      connect,
      disconnect,
      getConnection,
      getTransport,
      upgradeConnection,
      connections,
    ],
  );

  return (
    <Libp2pContext.Provider value={value}>{children}</Libp2pContext.Provider>
  );
};
